import type { PaginatedDocumentsResponse } from '@/types/catalog'

/**
 * DocumentFetchManager
 *
 * Singleton manager to deduplicate and debounce concurrent fetchDocuments() calls.
 *
 * Problems solved:
 * 1. Race conditions: Multiple sources (SSE events, user actions, upload completions)
 *    can trigger fetchDocuments() simultaneously, causing deleted files to reappear
 * 2. Duplicate API calls: Rapid successive calls waste bandwidth and create inconsistent state
 * 3. Request superseding: When sort params change, old requests should be aborted
 *
 * How it works:
 * - Deduplicates: Multiple concurrent calls with same params share the same Promise
 * - Debounces: Rapid successive calls are delayed and only the last executes
 * - Aborts: When params change, the previous request is aborted via AbortController
 */
class DocumentFetchManager {
  private pendingRequest: {
    promise: Promise<PaginatedDocumentsResponse>
    abortController: AbortController
    params: string
  } | null = null

  private debounceTimer: NodeJS.Timeout | null = null
  private readonly DEBOUNCE_MS = 250

  // Track all resolvers/rejectors waiting during debounce to avoid hanging promises
  private pendingResolvers: Array<{
    resolve: (value: PaginatedDocumentsResponse) => void
    reject: (reason?: unknown) => void
    params: string
  }> = []

  /**
   * Fetch documents with deduplication, debouncing, and abort support
   *
   * @param apiCall - Function that makes the actual API call, receives AbortSignal
   * @param queryParams - Serializable params object for deduplication
   * @param options.fresh - Require current state rather than accepting an
   *   in-flight response for the same params. See FS-601 note below.
   * @returns Promise resolving to paginated documents response
   */
  async fetch(
    apiCall: (signal: AbortSignal) => Promise<PaginatedDocumentsResponse>,
    queryParams?: Record<string, unknown>,
    options?: { fresh?: boolean }
  ): Promise<PaginatedDocumentsResponse> {
    const params = JSON.stringify(queryParams ?? {})

    // If same request is already executing (post-debounce), return the existing
    // promise — correct for concurrent identical *view* queries, which only want
    // the data once.
    //
    // FS-601: NOT correct for a refresh issued after a mutation ("show me what
    // the server has now"). Every SSE-triggered refetch uses identical params
    // (`refreshParamsFor`), so this branch used to hand a per-page P&ID
    // completion the snapshot taken at the FIRST page's completion — a payload
    // that predates the completion being refreshed for, and that then overwrote
    // the finished row. `fresh` opts those callers out; the debounce below still
    // collapses bursts, so this costs at most one extra request per burst.
    if (!options?.fresh && this.pendingRequest?.params === params) {
      return this.pendingRequest.promise
    }

    // A fresh read supersedes an in-flight identical request: abort it so its
    // stale payload can never be applied, and let the debounced path below
    // issue a new one. Waiting resolvers reject with AbortError, which
    // fetchDocuments already ignores without clearing the table.
    if (options?.fresh && this.pendingRequest?.params === params) {
      this.pendingRequest.abortController.abort()
      this.pendingRequest = null
    }

    // If a different request is pending, abort it and reject all waiting resolvers
    if (this.pendingRequest && this.pendingRequest.params !== params) {
      this.pendingRequest.abortController.abort()
      this.pendingRequest = null

      // Reject all resolvers waiting for the old params
      const abortError = new Error('Request superseded by new params')
      abortError.name = 'AbortError'
      this.pendingResolvers
        .filter((r) => r.params !== params)
        .forEach((r) => r.reject(abortError))
      this.pendingResolvers = this.pendingResolvers.filter(
        (r) => r.params === params
      )
    }

    // Clear existing debounce timer and reject old resolvers if params changed
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)

      // Check if there are pending resolvers with different params
      const hasOldResolvers = this.pendingResolvers.some(r => r.params !== params)
      if (hasOldResolvers) {
        // Reject all resolvers waiting for different params
        const abortError = new Error('Request superseded by new params')
        abortError.name = 'AbortError'
        this.pendingResolvers
          .filter((r) => r.params !== params)
          .forEach((r) => r.reject(abortError))
        this.pendingResolvers = this.pendingResolvers.filter(
          (r) => r.params === params
        )
      }
    }

    // Return a promise and track its resolver for later
    return new Promise((resolve, reject) => {
      // Add this resolver to the pending list
      this.pendingResolvers.push({ resolve, reject, params })

      // Set debounce timer - will execute the actual fetch after delay
      this.debounceTimer = setTimeout(async () => {
        this.debounceTimer = null

        // Get all resolvers for this params (might be multiple concurrent calls)
        const resolversForThisRequest = this.pendingResolvers.filter(
          (r) => r.params === params
        )
        // Clear them from pending list
        this.pendingResolvers = this.pendingResolvers.filter(
          (r) => r.params !== params
        )

        const controller = new AbortController()
        const promise = apiCall(controller.signal)

        this.pendingRequest = { promise, abortController: controller, params }

        try {
          const result = await promise
          // Clear pending request on success.
          // FS-601: identified by CONTROLLER, not params. A `fresh` read can
          // supersede an identical-params request, so two requests with the same
          // params now overlap — and a params-only check would have the aborted
          // one clear its replacement's entry, leaving a live request invisible
          // to hasPending() and to dedup.
          if (this.pendingRequest?.abortController === controller) {
            this.pendingRequest = null
          }
          // FS-601: a superseded request must never deliver its payload, even if
          // the api call ignored the abort signal. Aborting only guarantees the
          // *intent* to discard; without this check a resolved-but-aborted
          // response still reaches the store and can overwrite newer state.
          if (controller.signal.aborted) {
            const abortError = new Error('Request superseded')
            abortError.name = 'AbortError'
            resolversForThisRequest.forEach((r) => r.reject(abortError))
            return
          }
          // Resolve ALL waiting promises with the same result
          resolversForThisRequest.forEach((r) => r.resolve(result))
        } catch (error) {
          // Clear pending request on error (by controller — see above)
          if (this.pendingRequest?.abortController === controller) {
            this.pendingRequest = null
          }
          // Reject ALL waiting promises with the same error
          resolversForThisRequest.forEach((r) => r.reject(error))
        }
      }, this.DEBOUNCE_MS)
    })
  }

  /**
   * Cancel any pending request
   * Useful for cleanup when component unmounts
   */
  cancelPending(): void {
    if (this.pendingRequest) {
      this.pendingRequest.abortController.abort()
      this.pendingRequest = null
    }

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }

    // Reject all pending resolvers
    const abortError = new Error('Request cancelled')
    abortError.name = 'AbortError'
    this.pendingResolvers.forEach((r) => r.reject(abortError))
    this.pendingResolvers = []
  }

  /**
   * Check if there's a pending request
   * Useful for debugging
   */
  hasPending(): boolean {
    return (
      this.pendingRequest !== null ||
      this.debounceTimer !== null ||
      this.pendingResolvers.length > 0
    )
  }
}

// Export singleton instance
export const documentFetchManager = new DocumentFetchManager()
